import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Link, Stack, Chip, IconButton, Button, Alert } from '@mui/material';
import {
  AccountTreeOutlined,
  CalendarTodayOutlined,
  WarningAmberOutlined,
  MoreVertOutlined,
} from '@mui/icons-material';
import GlobalMaterialTable from '../../components/common/GlobalMaterialTable';
import { getPromptSlots } from '../../services/promptConfigurationService';

const formatDateTime = (value) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

const normalizePromptSlot = (slot) => ({
  step_key: slot.slot_key || '',
  workflow: slot.workflow_name || '',
  active_rules: slot.active_rules ?? slot.active_rule_count ?? null,
  cap: Number(slot.max_active_rules) || 0,
  version: slot.version ?? '',
  last_updated: formatDateTime(slot.updated_at),
  updated_by: slot.base_prompt_updated_by || '-',
});

const PromptSteps = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [workflowFilter, setWorkflowFilter] = useState('all');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  useEffect(() => {
    let isMounted = true;

    const loadPromptSlots = async () => {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const slots = await getPromptSlots();

        if (!isMounted) return;

        setRows(slots.map((slot) => normalizePromptSlot(slot)));
      } catch (error) {
        if (!isMounted) return;
        // setErrorMessage(
        //   error?.response?.data?.message || error?.message || 'Unable to load prompt steps.'
        // );
        setRows([]);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadPromptSlots();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredData = useMemo(
    () =>
      rows.filter((row) => {
        const matchesSearch = row.step_key.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesWorkflow = workflowFilter === 'all' || row.workflow === workflowFilter;
        return matchesSearch && matchesWorkflow;
      }),
    [rows, searchTerm, workflowFilter]
  );

  const workflowCount = useMemo(
    () => new Set(rows.map((row) => row.workflow).filter(Boolean)).size,
    [rows]
  );

  const columns = useMemo(
    () => [
      {
        header: 'Step Key',
        accessorKey: 'step_key',
        Cell: ({ row }) => (
          <Link
            component="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/prompt-rules/${row.original.step_key}`);
            }}
            sx={{ textDecoration: 'none', color: '#1d8ef0', fontWeight: 500 }}
          >
            {row.original.step_key}
          </Link>
        ),
      },
      {
        header: 'Workflow',
        accessorKey: 'workflow',
      },
      {
        header: 'Active Rules',
        accessorKey: 'active_rules',
        Cell: ({ row }) => {
          const cap = row.original.cap || 0;
          const activeRules = row.original.active_rules;
          const hasActiveRules = activeRules !== null && activeRules !== undefined;
          const nearCap = cap > 0 && hasActiveRules && activeRules >= cap - 1;
          return (
            <Chip
              label={`${hasActiveRules ? activeRules : '-'} / ${cap || '-'}`}
              size="small"
              sx={{
                height: 20,
                minWidth: 54,
                px: 0.25,
                borderRadius: '10px',
                bgcolor: nearCap ? '#FFF1F2' : '#F0FDF4',
                color: nearCap ? '#E11D48' : '#15803D',
                border: '1px solid',
                borderColor: nearCap ? '#FFE4E6' : '#DCFCE7',
                fontWeight: 600,
                fontSize: '0.72rem',

                '& .MuiChip-label': {
                  px: 1,
                  py: 0,
                },
              }}
            />
          );
        },
      },
      {
        header: 'Version',
        accessorKey: 'version',
      },
      {
        header: 'Last Updated',
        accessorKey: 'last_updated',
        Cell: ({ row }) => (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ color: '#334155' }}>
            <CalendarTodayOutlined sx={{ fontSize: 16, color: '#64748b' }} />
            <Typography variant="body2">{row.original.last_updated}</Typography>
          </Stack>
        ),
      },
      {
        header: 'Updated By',
        accessorKey: 'updated_by',
        Cell: ({ row }) => (
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="body2" sx={{ color: '#334155' }}>
              {row.original.updated_by}
            </Typography>
          </Stack>
        ),
      },
      {
        header: 'Actions',
        id: 'actions',
        Cell: ({ row }) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/prompt-rules/${row.original.step_key}`);
              }}
              sx={{
                textTransform: 'none',
                borderColor: '#e2e8f0',
                color: '#1d8ef0',
                borderRadius: 1.5,
                py: 0.25,
              }}
            >
              View
            </Button>
            <IconButton size="small" sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
              <MoreVertOutlined fontSize="small" sx={{ color: '#64748b' }} />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [navigate]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 100px)' }}>
      <Box sx={{ flex: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Paper
            elevation={0}
            sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden' }}
          >
            {errorMessage && (
              <Alert severity="error" sx={{ borderRadius: 0 }}>
                {errorMessage}
              </Alert>
            )}
            {/* Table */}
            <GlobalMaterialTable
              isLoading={isLoading}
              columns={columns}
              data={filteredData}
              emptyMessage="No Prompt Steps found"
              enablePagination
              enableBottomToolbar
              manualPagination={false}
              page={pagination.pageIndex + 1}
              pageSize={pagination.pageSize}
              onPageChange={(p) => setPagination((prev) => ({ ...prev, pageIndex: p - 1 }))}
              onPageSizeChange={(ps) => setPagination((prev) => ({ ...prev, pageSize: ps }))}
              tableTitle=""
              renderTopToolbarCustomActions={() => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: '#f0f9ff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <AccountTreeOutlined sx={{ fontSize: 20, color: '#1d8ef0' }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, fontSize: '16px', color: '#1e293b', lineHeight: 1.2 }}
                    >
                      Prompt Steps
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#64748b', mt: 0.5, fontSize: '12px' }}
                    >
                      {rows.length} steps across {workflowCount} workflows
                    </Typography>
                  </Box>
                </Box>
              )}
              muiTableBodyRowProps={({ row }) => ({
                onClick: () => navigate(`/prompt-rules/${row.original.step_key}`),
                sx: { cursor: 'pointer' },
              })}
              muiTablePaperProps={{
                elevation: 0,
                sx: {
                  border: 'none !important',
                  boxShadow: 'none !important',
                },
              }}
              muiBottomToolbarProps={{
                sx: {
                  minHeight: '48px',
                  py: 0,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  '& > .MuiBox-root': {
                    width: '100%',
                    justifyContent: 'flex-end',
                  },
                  '& .MuiTablePagination-root': {
                    marginLeft: 'auto',
                  },
                  '& .MuiToolbar-root': {
                    minHeight: '48px',
                    paddingTop: '4px',
                    paddingBottom: '4px',
                    justifyContent: 'flex-end',
                  },
                },
              }}
              renderBottomToolbarCustomActions={() =>
                filteredData.some((r) => r.cap > 0 && r.active_rules !== null && r.active_rules !== undefined && r.active_rules >= r.cap - 1) ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1.25,
                      py: 0.75,
                      mx: 1,
                      mb: 1,
                      borderRadius: 1,
                      bgcolor: '#FFFBEB',
                    }}
                  >
                    <WarningAmberOutlined
                      sx={{
                        fontSize: 15,
                        color: '#D97706',
                        flexShrink: 0,
                      }}
                    />

                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.73rem',
                        color: '#6B7280',
                        lineHeight: 1.45,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 600,
                          color: '#92400E',
                        }}
                      >
                        {filteredData
                          .filter((r) => r.cap > 0 && r.active_rules !== null && r.active_rules !== undefined && r.active_rules >= r.cap - 1)
                          .map((r) => r.step_key)
                          .join(', ')}
                      </Box>{' '}
                      is approaching its rule limit. Consider consolidating existing rules before
                      adding new ones.
                    </Typography>
                  </Box>
                ) : null
              }
            />
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default PromptSteps;

